/**
 * What makes a session "flagged" — shared by the Sessions list's Errors chip
 * and the sidebar badge that links to it.
 *
 * These MUST agree. A badge that says 1 next to a list that shows 3 (or 0)
 * is worse than no badge at all, and they drifted apart the moment the rule
 * lived in two places.
 *
 * Two flags carry a failure today:
 *   - `camera_refund_required` — written by the cloud's liveness check, or by
 *     the kiosk through `reportCameraFailure` when the camera dies before the
 *     first photo and there is no session doc yet.
 *   - `print_refund_required` — written by the kiosk when printing fails.
 *
 * A session leaves the queue two ways:
 *   - `refund_status: 'completed'` — the money went back by itself
 *   - `flag_resolved_at` — an admin settled it another way and said so
 *     (`resolveSessionFlag`). Credits sessions have no card charge to
 *     reverse, so without this they would sit on the badge forever.
 *
 * The flags themselves are never cleared: they are the record of what
 * happened to that customer. This is a work list, not a history — a settled
 * failure is still findable by ID or through the Payments page.
 */
export const SESSION_FLAGS = ['camera_refund_required', 'print_refund_required'];

/** Ceiling on each per-flag query. Flagged sessions are rare by construction;
 *  if this truncates, the list says so rather than showing a partial queue. */
export const FLAGGED_QUERY_LIMIT = 500;

export const isFlaggedSession = (session) =>
  SESSION_FLAGS.some((flag) => session?.[flag] === true) &&
  session?.refund_status !== 'completed' &&
  !session?.flag_resolved_at;

/** Which failure to name on the row. Camera wins when a session has both:
 *  it is the one that cost the customer their photos entirely. */
export const flagLabel = (session) => {
  if (session?.camera_refund_required) return 'Camera failed';
  if (session?.print_refund_required) return 'Print failed';
  return null;
};
